/* global console */

import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store/store";
import { subscribeToHost } from "../../../utils/helpers/EventsHelper";
import {
  CallbackElaborationsSnapshotStatus,
  CallbackElaborationStatus,
  CallbackHeartbeatStatus,
  CallbackSingleElaborationItemStatus,
  createElaborationRequest,
  ElabResultType,
} from "../../../api/object/RefreshObject";
import NotificationManager from "../../../common/notification/NotificationManager";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import { handleState } from "../../../utils/helpers/ElaborationHelper";

export interface ElaborationStatus {
  correlationId: string;
  clientId: string;
  documentId: string;
  userId: string;
  items: ElaborationStatusItem[];
}

export interface ElaborationStatusItem {
  id: string;
  contentControlId: string;
  status:
    | "REQUESTED"
    | "STARTED"
    | "REJECTED"
    | "REJECTED_FOR_CONCURRENCY"
    | "FAILED"
    | "COMPLETED"
    | "REJECTED_FOR_OBJECT_NOT_FOUND"
    | "ABORTED";
  startDate: string;
  endDate: string;
  elabResult: string;
  elabResultType: ElabResultType;
  correlationId: string;
  formSheetAreaId: ElaborationFormSheetArea;
  resultAdded: boolean;
  userChecked: boolean;
  messages: ElaborationMessage[];
  elaborationType?:
    | "ADD"
    | "EDIT"
    | "REFRESH"
    | "EXPORT"
    | "IMPORT"
    | "DUPLICATE"
    | "CHANGE_DOC_STRUCTURE"
    | "DETACH"
    | "CLONE"
    | "DOWNLOAD_COPY"
    | "SHEET_AND_AREA";
  objectName: string;
}

export function elaborationStatusItemIsRunning(item: ElaborationStatusItem) {
  return item?.status === "STARTED" || item?.status === "REQUESTED";
}

export function elaborationStatusItemIsNotRunningAndNotUserChecked(item: ElaborationStatusItem) {
  return !elaborationStatusItemIsRunning(item) && !elaborationStatusItemIsUserChecked(item);
}

export function elaborationStatusItemIsToAdd(item: ElaborationStatusItem) {
  return item?.status === "COMPLETED" && !item.resultAdded;
}

export function elaborationStatusItemIsAdded(item: ElaborationStatusItem) {
  return item.status === "COMPLETED" && item.resultAdded;
}

export function elaborationStatusItemIsToHandleAbort(item: ElaborationStatusItem) {
  return item?.status === "ABORTED" && !item.resultAdded;
}

export function elaborationStatusItemIsCompleted(item: ElaborationStatusItem) {
  return item.status === "COMPLETED";
}

export function elaborationStatusItemIsRejected(item: ElaborationStatusItem) {
  return item.status === "REJECTED" || item.status === "REJECTED_FOR_CONCURRENCY";
}

export function elaborationStatusItemIsAborted(item: ElaborationStatusItem) {
  return item.status === "ABORTED" && !elaborationStatusItemIsUserChecked(item);
}

export function elaborationStatusItemIsEndButNotCompleted(item: ElaborationStatusItem) {
  return item.status === "FAILED";
}

export function elaborationStatusItemIsNotFound(item: ElaborationStatusItem) {
  return item.status === "REJECTED_FOR_OBJECT_NOT_FOUND";
}

export function elaborationStatusItemIsUserChecked(item: ElaborationStatusItem) {
  return item.userChecked;
}

export function elaborationStatusItemHasWarning(item: ElaborationStatusItem) {
  return item.messages && item.messages.filter((i) => i.severity === "WARNING").length > 0;
}

export const elaborationStatusItemIsBlocking = (
  item: ElaborationStatusItem,
  addedElaborationItemsList: { correlationId: string; itemId: string }[]
) => {
  return (
    elaborationStatusItemIsRunning(item) ||
    (elaborationStatusItemIsToAdd(item) &&
      !elaborationStatusItemIsUserChecked(item) &&
      addedElaborationItemsList.every(
        (addedItemDesc) => addedItemDesc.correlationId !== item.correlationId && addedItemDesc.itemId !== item.id
      ) &&
      !elaborationStatusItemIsUserChecked(item))
  );
};

export interface ElaborationFormSheetArea {
  dbId: string;
  formId: string;
  sheet: string;
  area: string;
}

export interface ElaborationMessage {
  text: string;
  details: string;
  severity: string;
  traceId: string;
}

export interface OpenEventChannelInput {
  baseUrl: string;
  documentId: string;
  userId: string;
  clientId: string;
}

// State and selectors ---
interface RemoteElaborationsState {
  elaborationStatusList: ElaborationStatus[];
  addedElaborationItemsList: { correlationId: string; itemId: string }[];
  lastReceivedEventTimestampMs?: number; //as unix timestamp
}

const initialState: RemoteElaborationsState = {
  elaborationStatusList: undefined,
  addedElaborationItemsList: [],
  lastReceivedEventTimestampMs: undefined,
};

export const createElaborationRequestAsync = createElaborationRequest;

const SEE_NOTIFICATION_KEY = "SEE_NOTIFICATION_KEY";

export const openEventChannelAsync = createAsyncThunk<any, OpenEventChannelInput>(
  "remoteElaborations/openEventChannel",
  (input, { dispatch, getState }) => {
    subscribeToHost({
      paramPath: `${input.baseUrl}/elaboration/status/${input.userId}/${input.documentId}/${input.clientId}`,
      openCallback: () => {
        console.log(`sse opened`);
      },
      messageCallback: (
        message: CallbackElaborationStatus | CallbackElaborationsSnapshotStatus | CallbackSingleElaborationItemStatus
      ) => {
        try {
          console.log("received message", message);
          dispatch(updateElaboration(message));
          handleState(getState() as RootState);
        } catch (e) {
          console.log(`error handling message ${e.message}`);
        }
      },
      errorCallback: (e) => {
        dispatch(resetStatusList());
        console.log(`received sse completion/error`, e);
        if (e.type === "error") {
          switch (e.message) {
            case "Failed to fetch":
              addNotification("cof.base.message.connectionerror", "cof.base.message.detail.connectionerror", "error");
              break;
            case "Failed to parse data":
              addNotification("cof.base.message.parsererror", "cof.base.message.detail.parsererror", "error");
              break;
            default:
              console.log("Error on callback " + e.message);
          }
        }
      },
    });
  }
);

const addNotification = (title: string, message: string, type: "success" | "error" | "warning" | "info") => {
  NotificationManager.addNotification({
    title: formatMessage(title),
    message: formatMessage(message),
    key: SEE_NOTIFICATION_KEY,
    type: type,
    closable: true,
    removeOnClose: true,
    isTemporary: false,
  });
};

function isSnapshot(
  message:
    | CallbackElaborationStatus
    | CallbackElaborationsSnapshotStatus
    | CallbackSingleElaborationItemStatus
    | CallbackHeartbeatStatus
): message is CallbackElaborationsSnapshotStatus {
  return (message as CallbackElaborationsSnapshotStatus).snapshot !== undefined;
}

function isSingleItem(
  message:
    | CallbackElaborationStatus
    | CallbackElaborationsSnapshotStatus
    | CallbackSingleElaborationItemStatus
    | CallbackHeartbeatStatus
): message is CallbackElaborationsSnapshotStatus {
  return (message as CallbackSingleElaborationItemStatus).singleItem !== undefined;
}

function isElaborationStatus(
  message:
    | CallbackElaborationStatus
    | CallbackElaborationsSnapshotStatus
    | CallbackSingleElaborationItemStatus
    | CallbackHeartbeatStatus
): message is CallbackElaborationStatus {
  return (message as CallbackElaborationStatus).items !== undefined;
}

const remoteElaborationsSlice = createSlice({
  name: "remoteElaborations",
  initialState,
  reducers: {
    removeElaborationItem: (state, action: PayloadAction<ElaborationStatusItem>) => {
      const event = action.payload;
      const previousList: ElaborationStatus[] = state.elaborationStatusList ?? [];
      state.elaborationStatusList = previousList.map((elab) => {
        elab.items = elab.items.filter((item) => item.id != event.id);
        return elab;
      });
      console.log("updated elaborations", state.elaborationStatusList);
    },
    flagElaborationItemResultAdded: (
      state: RemoteElaborationsState,
      action: PayloadAction<{ correlationId: string; itemId: string }>
    ) => {
      state.elaborationStatusList = state.elaborationStatusList?.map((el) => {
        if (el.correlationId !== action.payload.correlationId && el.items)
          return {
            ...el,
            items: el.items.map((item) => {
              if (item.id === action.payload.itemId) return { ...item, resultAdded: true };
              return item;
            }),
          };
        return el;
      });
      state.addedElaborationItemsList.push(action.payload);
      console.log("flagElaborationResultAdded elaborations", state.elaborationStatusList, action.payload);
    },
    flagElaborationUserChecked: (state: RemoteElaborationsState, action: PayloadAction<{ correlationId: string }>) => {
      state.addedElaborationItemsList = state.addedElaborationItemsList.filter(
        (detail) => detail.correlationId !== action.payload.correlationId
      );
      console.log("flagElaborationUserChecked elaborations", state.elaborationStatusList, action.payload);
    },
    resetStatusList: (state) => {
      state.elaborationStatusList = undefined;
      console.log("reset elaborations", state.elaborationStatusList);
    },
    updateElaboration: (
      state,
      action: PayloadAction<
        | CallbackElaborationStatus
        | CallbackElaborationsSnapshotStatus
        | CallbackSingleElaborationItemStatus
        | CallbackHeartbeatStatus
      >
    ) => {
      // console.log("previous elaborations", state.elaborationStatusList);
      const event = action.payload;
      state.lastReceivedEventTimestampMs = new Date().getTime();
      if (isSnapshot(event)) {
        state.elaborationStatusList = event.snapshot;
        // Fix for missing correlationId
        state.elaborationStatusList.flatMap((e) => {
          e.items.forEach((i) => (i.correlationId = e.correlationId));
          return e.items;
        });
        console.log("snapshot elaborations", JSON.parse(JSON.stringify(state.elaborationStatusList)));
      } else if (isSingleItem(event)) {
        const receivedItem = event as CallbackSingleElaborationItemStatus;
        const previousList = state.elaborationStatusList ?? [];
        const targetElaboration = previousList.find((e) => e.correlationId == receivedItem.correlationId);
        targetElaboration.items = targetElaboration.items.filter((i) => i.id != receivedItem.singleItem.id);
        targetElaboration.items.push(receivedItem.singleItem);
        console.log("updated elaborations with single item", JSON.parse(JSON.stringify(state.elaborationStatusList)));
      } else if (isElaborationStatus(event)) {
        const receivedElaboration = event as CallbackElaborationStatus;
        // Fix for missing correlationId
        receivedElaboration?.items.forEach((e) => {
          e.correlationId = receivedElaboration.correlationId;
          return e;
        });
        const previousList = state.elaborationStatusList ?? [];
        const currentList = previousList.filter((e) => e.correlationId != receivedElaboration.correlationId);
        if (receivedElaboration.items.length > 0) {
          //FIX IDS-1500 - An Empty Elaboration can be ignored end excluded from the list
          currentList.push(receivedElaboration);
        }
        state.elaborationStatusList = currentList;
        console.log("updated elaborations with elaboration", JSON.parse(JSON.stringify(state.elaborationStatusList)));
      }
    },
  },
});

export const selectElaborationStatusList = (state: RootState) => state.remoteElaborations.elaborationStatusList;

export const selectElaborationsWithNotRunningAndNotCheckedItems = (state: RootState) =>
  (state.remoteElaborations.elaborationStatusList ?? []).filter((elaboration) =>
    elaboration.items.some((item) => elaborationStatusItemIsNotRunningAndNotUserChecked(item))
  );

export const selectAbortedElaborations = (state: RootState) =>
  (state.remoteElaborations.elaborationStatusList ?? []).filter(
    (elaboration) =>
      elaboration.items.filter((item) => elaborationStatusItemIsAborted(item)).length == elaboration.items.length
  );

export const selectNotRunningAndNotUserCheckedElaborationStatusItemList = (state: RootState) =>
  (state.remoteElaborations.elaborationStatusList ?? [])
    .filter(
      (elaboration) =>
        elaboration.items.filter((item) => elaborationStatusItemIsNotRunningAndNotUserChecked(item)).length > 0
    )
    .flatMap((elaboration) => elaboration.items);

export const {
  flagElaborationItemResultAdded,
  flagElaborationUserChecked,
  resetStatusList,
  updateElaboration,
  removeElaborationItem,
} = remoteElaborationsSlice.actions;

export default remoteElaborationsSlice.reducer;
